import { API_BASE } from '../config';
import { useEffect, useState, useCallback } from 'react';
import { Server, Database, Users, Wifi, Clock, HardDrive, Activity, FileText, Calendar } from 'lucide-react';

interface ServerStatsData {
  connections: {
    activeAgents: number;
    frontendClients: number;
    liveStatsSockets: number;
  };
  database: {
    sizeBytes: number;
    devices: number;
    statsRows: number;
    scheduledTasks: number;
    scripts: number;
  };
  admins: number;
  serverUptime: number;
}

function formatBytes(bytes: number) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024, dm = 2, sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

function formatUptime(seconds: number) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
}

export default function ServerStats() {
  const [data, setData] = useState<ServerStatsData | null>(null);
  const [error, setError] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/server-stats`, { credentials: 'include' });
      if (res.ok) {
        setData(await res.json());
        setError('');
      } else {
        setError('Failed to load server stats');
      }
    } catch {
      setError('Failed to connect to server');
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 10000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (error) {
    return (
      <div className="glass-panel" style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger)' }}>
        {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="glass-panel" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
        Loading server stats...
      </div>
    );
  }

  const statCard = (icon: React.ReactNode, label: string, value: string | number, color: string) => (
    <div className="glass-panel" style={{ padding: '1.25rem', flex: '1 1 200px', minWidth: '200px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <div style={{ color }}>{icon}</div>
        <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--text-primary)' }}>{value}</div>
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <Server size={24} color="var(--accent-primary)" />
        <h2 style={{ margin: 0, fontWeight: 600 }}>Server Statistics</h2>
      </div>

      {/* Connections */}
      <div>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <Wifi size={16} /> Active Connections
        </h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {statCard(<Activity size={22} />, 'Connected Agents', data.connections.activeAgents, 'var(--success)')}
          {statCard(<Users size={22} />, 'Frontend Clients', data.connections.frontendClients, 'var(--accent-primary)')}
          {statCard(<Wifi size={22} />, 'Live Stats Sockets', data.connections.liveStatsSockets, '#f59e0b')}
        </div>
      </div>

      {/* Server Info */}
      <div>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <Server size={16} /> Server Info
        </h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {statCard(<Users size={22} />, 'Admin Accounts', data.admins, 'var(--accent-primary)')}
          {statCard(<Clock size={22} />, 'Server Uptime', formatUptime(data.serverUptime), 'var(--success)')}
        </div>
      </div>

      {/* Database */}
      <div>
        <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, marginBottom: '1rem', color: 'var(--text-secondary)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          <Database size={16} /> Database
        </h3>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          {statCard(<HardDrive size={22} />, 'Database Size', formatBytes(data.database.sizeBytes), '#a855f7')}
          {statCard(<Server size={22} />, 'Devices', data.database.devices, 'var(--accent-primary)')}
          {statCard(<Activity size={22} />, 'Stats Rows', data.database.statsRows.toLocaleString(), 'var(--success)')}
          {statCard(<Calendar size={22} />, 'Scheduled Tasks', data.database.scheduledTasks, '#f59e0b')}
          {statCard(<FileText size={22} />, 'Scripts', data.database.scripts, '#0ea5e9')}
        </div>
      </div>
    </div>
  );
}
